import { z } from 'zod';
import { deliveryPreferenceSchema } from './delivery-preference';

// Read models for the current backend only.
export const entityIdSchema = z.string().regex(/^(0x)?[0-9a-fA-F]{32}$/);
const responseId = z.string().regex(/^0x[0-9a-f]{32}$/);
const account = z.string().regex(/^0x[0-9a-f]{64}$/);
export const u32 = z.number().int().min(0).max(4294967295);
export const moneySchema = z
  .string()
  .refine(
    (value) =>
      /^(0|[1-9][0-9]{0,19})$/.test(value) &&
      BigInt(value) <= 18446744073709551615n,
    'Expected exact integer KVN units.'
  );
const week = z.object({
  isoYear: z.number().int().min(-2147483648).max(2147483647),
  week: z.number().int().min(1).max(53),
});
const windowSchema = z.object({ start: week, end: week });
const revision = z.number().int().nonnegative().safe();
export const scoreSchema = z.number().int().min(1).max(5);
export const evidenceSchema = z
  .object({
    url: z.string().refine((value) => {
      if (
        new TextEncoder().encode(value).length > 2048 ||
        /\s/u.test(value) ||
        [...value].some(
          (char) =>
            char.charCodeAt(0) < 32 ||
            (char.charCodeAt(0) >= 127 && char.charCodeAt(0) <= 159)
        ) ||
        !value.startsWith('https://')
      )
        return false;
      try {
        const url = new URL(value);
        return (
          url.protocol === 'https:' &&
          !!url.hostname &&
          !url.username &&
          !url.password
        );
      } catch {
        return false;
      }
    }, 'Enter an absolute HTTPS URL without credentials or whitespace (maximum 2048 bytes).'),
    // Accept obsolete fields on old records, then discard them.
    sha256: z.unknown().optional(),
  })
  .strict()
  .transform(({ url }) => ({ url }));
export const workerRatingSchema = z
  .object({ worker: account, score: scoreSchema })
  .strict();
export const completionSubmissionSchema = z.object({
  submissionId: responseId,
  version: revision,
  deliverable: evidenceSchema.nullable(),
  submittedBy: account,
  submittedAt: revision,
  workerRatings: z.array(workerRatingSchema),
  review: z.discriminatedUnion('type', [
    z.object({ type: z.literal('PendingReview') }),
    z.object({
      type: z.literal('Rejected'),
      reason: evidenceSchema,
      reviewedBy: account,
      reviewedAt: revision,
    }),
    z.object({
      type: z.literal('RejectedWithComment'),
      commentId: responseId,
      reviewedBy: account,
      reviewedAt: revision,
    }),
    z.object({
      type: z.literal('Accepted'),
      reviewedBy: account,
      reviewedAt: revision,
    }),
  ]),
});
export const milestoneStatusSchema = z.enum([
  'NotStarted',
  'InProgress',
  'CompletionRequested',
  'Completed',
  'Disputed',
  'ChangesRequested',
]);
export const taskSchema = z.object({
  taskId: u32,
  reporter: account.optional(),
  createdAt: revision.optional(),
  updatedAt: revision.optional(),
  task: z.object({
    title: z.string(),
    description: z.string(),
    taskType: z.enum(['Feature', 'Bug', 'Task', 'Epic', 'Story']),
    priority: z.enum(['Lowest', 'Low', 'Medium', 'High', 'Highest', 'Blocker']),
    status: z.enum([
      'ToDo',
      'Open',
      'InProgress',
      'InReview',
      'Done',
      'Closed',
    ]),
    assignees: z.array(account),
    estimatedMinutes: u32,
    loggedMinutes: u32,
    dueAt: z.number().int().nonnegative().safe().nullable().optional(),
  }),
});
export const milestoneSchema = z.object({
  milestoneId: responseId,
  definition: z.object({
    key: u32,
    title: z.string(),
    window: windowSchema,
    coordinatorFee: moneySchema,
    coordinatorMinutes: u32,
    requirements: z.array(
      z.object({
        key: u32,
        roleId: u32.min(1),
        skillIds: z.array(u32.min(1)),
        minutes: u32,
        budget: moneySchema,
      })
    ),
  }),
  status: milestoneStatusSchema.nullable(),
  assignments: z.array(z.object({ requirementKey: u32, worker: account })),
  taskStorage: z.object({
    taskStorageId: responseId,
    milestoneId: responseId,
    tasks: z.array(taskSchema),
  }),
  frozen: z.boolean(),
  workerRatings: z.array(workerRatingSchema),
  submissions: z.array(completionSubmissionSchema),
});
export const planningQuoteSchema = z.object({
  fee: moneySchema,
  minutes: u32,
  window: windowSchema,
});
export const compensationPolicySchema = z.object({
  coordinatorPercent: z.number().int().min(0).max(100),
});
export const projectViewSchema = z.object({
  projectId: responseId,
  createdAt: z.number().int().nonnegative().safe().nullable().default(null),
  client: account,
  coordinator: account,
  title: z.string(),
  description: z.string(),
  planning: z.object({
    revision,
    status: z.enum([
      'AwaitingQuote',
      'Quoted',
      'Accepted',
      'Delivered',
      'Completed',
      'Disputed',
    ]),
    quote: planningQuoteSchema.nullable(),
    escrow: moneySchema,
    frozen: z.boolean(),
  }),
  proposals: z.array(
    z.object({
      proposalId: responseId,
      revision,
      title: z.string(),
      description: z.string(),
      status: z.enum(['Draft', 'PendingApproval', 'Approved', 'Cancelled']),
      milestones: z.array(milestoneSchema),
      changeRequest: z.string().nullable(),
      delivery: deliveryPreferenceSchema.nullable().default(null),
      compensationPolicy: compensationPolicySchema.nullable().default(null),
    })
  ),
  executionEscrow: moneySchema,
  cancelled: z.boolean(),
  completed: z.boolean(),
  evaluations: z
    .array(
      z.object({
        author: account,
        submittedAt: revision,
        ratings: z.array(z.object({ account, score: scoreSchema })),
      })
    )
    .default([]),
  activeDisputeId: responseId.nullable(),
});
export type ProjectView = z.infer<typeof projectViewSchema>;
export type MilestoneView = z.infer<typeof milestoneSchema>;
export type ProposalView = ProjectView['proposals'][number];
export type WeekWindow = z.infer<typeof windowSchema>;
